import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { UtilisateurTaches } from '@europrocurement/l2d-domain';
import { DossierTaches } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import { PrescripteurTaches } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { dossiersBasePath } from '@europrocurement/l2d-modules/modules/Dossiers';
import { useNavigate } from 'react-router-dom';
import { prescripteursBasePath } from '@europrocurement/l2d-modules/modules/PrescriberClient/constants/paths';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import useTasksHook from '../../hook/useTasksHook';

export type ActionMenuProps = {
    item: DossierTaches | PrescripteurTaches | UtilisateurTaches;
};

const TaskActionMenu: React.FunctionComponent<ActionMenuProps> = function ({ item }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const open = Boolean(anchorEl);
    const keycloakService = UseKeycloakService();
    const currentUserIdentifier = keycloakService.getTokenParsed()?.preferred_username;
    const { palette } = useTheme();
    const [currentEntityLinked, setCurrentEntityLinked] = useState<
        'tiers' | 'formalite' | 'utilisateur'
    >('utilisateur');

    const { handleEditTask, handleCheckTask, handleDeleteTask } = useTasksHook({
        apiTarget: currentEntityLinked,
        linkedEntityId: item.id ? item.id.toString() : '',
    });

    const handleDossierNavigation = useCallback(
        ({ dossier }: DossierTaches) => {
            if (dossier.id) {
                navigate(`/${dossiersBasePath}/${dossier.id}?dossier-detail=activities`);
            }
        },
        [navigate],
    );

    const handleTiersNavigation = useCallback(
        ({ tiers }: PrescripteurTaches) => {
            if (tiers.id) {
                navigate(`/${prescripteursBasePath}/liste/${tiers.id}`);
            }
        },
        [navigate],
    );

    useEffect(() => {
        if (Object.hasOwn(item, 'tiers')) {
            setCurrentEntityLinked('tiers');
        } else if (Object.hasOwn(item, 'dossier')) {
            setCurrentEntityLinked('formalite');
        }
    }, [item]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const defaultTaskMenuItems: React.JSX.Element[] = [
        <MenuItem
            key="edit-task"
            onClick={() => {
                handleEditTask(item);
            }}
            sx={{ gap: '4px' }}
        >
            <FaOptionIcon
                icon={['fasl', 'edit']}
                color={palette.grey['700']}
            />
            <Typography sx={{ color: 'text.primary' }}>Editer</Typography>
        </MenuItem>,
        <MenuItem
            key="check-task"
            onClick={() => {
                handleCheckTask(item);
            }}
            sx={{ gap: '4px' }}
        >
            <FaOptionIcon
                icon={['fasl', 'check']}
                color={palette.grey['700']}
            />
            <Typography sx={{ color: 'text.primary' }}>
                {item.tache?.isDone ? 'Dévalider' : 'Valider'}
            </Typography>
        </MenuItem>,
        ...(item.id && item.tache?.createdBy?.userIdentifier === currentUserIdentifier
            ? [
                  <MenuItem
                      key="delete-task"
                      onClick={() => {
                          handleDeleteTask(item.id as number);
                      }}
                      sx={{ gap: '4px' }}
                  >
                      <FaOptionIcon
                          icon={['fasl', 'trash']}
                          color={palette.error.main}
                      />
                      <Typography sx={{ color: palette.error.main }}>Supprimer</Typography>
                  </MenuItem>,
              ]
            : []),
    ];

    const dossierTasksMenuItems: React.JSX.Element[] = [
        <MenuItem
            key="show-task-dossier"
            onClick={() => {
                handleDossierNavigation(item as DossierTaches);
            }}
            sx={{ gap: '4px' }}
        >
            <FaOptionIcon
                icon={['fasl', 'eye']}
                color="text.gris70"
            />
            <Typography sx={{ color: 'text.primary' }}>Voir le dossier</Typography>
        </MenuItem>,
    ];

    const prescriberTasksMenuItems: React.JSX.Element[] = [
        <MenuItem
            key="show-task-tiers"
            onClick={() => {
                handleTiersNavigation(item as PrescripteurTaches);
            }}
            sx={{ gap: '4px' }}
        >
            <FaOptionIcon
                icon={['fasl', 'eye']}
                color="text.gris70"
            />
            <Typography sx={{ color: 'text.primary' }}>Voir le prescripteur</Typography>
        </MenuItem>,
    ];

    /**
     * Return array of menuItems, menuItems are merged form default menu and specific by apiTarget
     */
    const taskActionMenuItems = useMemo(() => {
        switch (currentEntityLinked) {
            case 'tiers':
                return [...prescriberTasksMenuItems, ...defaultTaskMenuItems];
            case 'formalite':
                return [...dossierTasksMenuItems, ...defaultTaskMenuItems];
            case 'utilisateur':
                return defaultTaskMenuItems;
            default:
                return defaultTaskMenuItems;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentEntityLinked]);

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
            }}
            onKeyDown={() => {}}
            role="button"
            tabIndex={0}
        >
            <IconButton
                aria-label="more"
                id="long-button"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
            >
                <FaOptionIcon
                    icon={['fasl', 'ellipsis-vertical']}
                    sx={{ fontSize: '1rem' }}
                />
            </IconButton>
            <Menu
                id="long-menu"
                MenuListProps={{
                    'aria-labelledby': 'long-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                {taskActionMenuItems.map((menuItem) => menuItem)}
            </Menu>
        </div>
    );
};

export default TaskActionMenu;
